import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';

const CALCULATIONS_DB_NAME = 'calculations-history';

@Injectable({ providedIn: 'root' })
export class IndexedDbService {
  private _db: IDBDatabase | null = null;
  private _pastTableName = 'past';
  private _futureTableName = 'future';
  private _databaseName = '';

  private _cleared = new Subject<void>();

  cleared$ = this._cleared.asObservable();

  initialize(): Promise<void> {
    return new Promise((resolve, reject) => {
      const clientId = uuidv4();
      this._databaseName = CALCULATIONS_DB_NAME + '_' + clientId;

      const request = indexedDB.open(this._databaseName, 1);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(this._pastTableName)) {
          db.createObjectStore(this._pastTableName, { keyPath: 'dbId', autoIncrement: true });
        }
        if (!db.objectStoreNames.contains(this._futureTableName)) {
          db.createObjectStore(this._futureTableName, { keyPath: 'dbId', autoIncrement: true });
        }
      };

      request.onsuccess = () => {
        this._db = request.result;

        // Ensure cleanup on unload
        window.addEventListener('beforeunload', () => {
          this._db?.close();
          indexedDB.deleteDatabase(this._databaseName);
        });

        resolve();
      };

      request.onerror = (e) => {
        const error = (e?.target as IDBRequest)?.error;
        reject(new Error(`Database open error: ${error?.message || error?.name || 'Unknown error'}`));
      };
    });
  }

  undo(currentValue: any): Promise<{
    record: any;
    pastCount: number;
    futureCount: number;
  }> {
    return new Promise((resolve, reject) => {
      if (!this._db) {
        return reject(`No database error`);
      }
      const tx = this._db.transaction([this._pastTableName, this._futureTableName], 'readwrite');
      const pastStore = tx.objectStore(this._pastTableName);
      const futureStore = tx.objectStore(this._futureTableName);

      const cursorReq = pastStore.openCursor(null, 'prev');

      cursorReq.onerror = (e) => {
        const error = (e?.target as IDBRequest)?.error;
        reject(`Cursor error: ${error ? error.message || error.name : 'Unknown error'}`);
      };

      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return resolve({ record: undefined, pastCount: 0, futureCount: 0 });

        const { dbId, ...lastRecord } = cursor.value;

        const insertPromise = new Promise<void>((res, rej) => {
          const req = futureStore.add(currentValue);
          req.onsuccess = () => res();
          req.onerror = (e) => rej(`Insert error: ${(e?.target as IDBRequest)?.error}`);
        });

        const deletePromise = new Promise<void>((res, rej) => {
          const req = cursor.delete();
          req.onsuccess = () => res();
          req.onerror = (e) => rej(`Delete error: ${(e?.target as IDBRequest)?.error}`);
        });

        insertPromise
          .then(() => deletePromise)
          .then(() => this._getStoreCounts(tx, [this._pastTableName, this._futureTableName]))
          .then(({ past, future }) => {
            resolve({
              record: lastRecord,
              pastCount: past,
              futureCount: future,
            });
          })
          .catch((error) => reject(`moveLastAndCount failed: ${error}`));
      };

      tx.onerror = (e) => reject(`Transaction error: ${(e?.target as IDBRequest)?.error}`);
    });
  }

  redo(record: any): Promise<{
    record: any;
    pastCount: number;
    futureCount: number;
  }> {
    return new Promise((resolve, reject) => {
      if (!this._db) {
        return reject(`No database error`);
      }

      const tx = this._db.transaction([this._pastTableName, this._futureTableName], 'readwrite');
      const futureStore = tx.objectStore(this._futureTableName);
      const pastStore = tx.objectStore(this._pastTableName);

      const cursorReq = futureStore.openCursor(null, 'prev');

      cursorReq.onerror = (e) => {
        const error = (e?.target as IDBRequest)?.error;
        reject(`Cursor error: ${error ? error.message || error.name : 'Unknown error'}`);
      };

      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return resolve({ record: undefined, pastCount: 0, futureCount: 0 });

        const { dbId, ...firstRecord } = cursor.value;

        const insertPromise = new Promise<void>((res, rej) => {
          const req = pastStore.add(record);
          req.onsuccess = () => res();
          req.onerror = (e) => rej(`Insert error: ${(e?.target as IDBRequest)?.error}`);
        });

        const deletePromise = new Promise<void>((res, rej) => {
          const req = cursor.delete();
          req.onsuccess = () => res();
          req.onerror = (e) => rej(`Delete error: ${(e?.target as IDBRequest)?.error}`);
        });

        insertPromise
          .then(() => deletePromise)
          .then(() => this._getStoreCounts(tx, [this._pastTableName, this._futureTableName]))
          .then(({ past, future }) => {
            resolve({
              record: firstRecord,
              pastCount: past,
              futureCount: future,
            });
          })
          .catch((error) => reject(`moveFirstAndCount failed: ${error}`));
      };

      tx.onerror = (e) => reject(`Transaction error: ${(e?.target as IDBRequest)?.error}`);
    });
  }

  add(record: any): Promise<{
    record: undefined;
    pastCount: number;
    futureCount: number;
  }> {
    return new Promise((resolve, reject) => {
      if (!this._db) {
        return reject(`No database error`);
      }

      const tx = this._db.transaction([this._pastTableName, this._futureTableName], 'readwrite');
      const pastStore = tx.objectStore(this._pastTableName);
      const futureStore = tx.objectStore(this._futureTableName);

      const addReq = pastStore.add(record);

      addReq.onerror = (e) => {
        const error = (e?.target as IDBRequest)?.error;
        reject(`Add error: ${error ? error.message || error.name : 'Unknown error'}`);
      };

      addReq.onsuccess = () => {
        const clearReq = futureStore.clear();

        clearReq.onerror = (e) => {
          const error = (e?.target as IDBRequest)?.error;
          reject(`Clear error: ${error ? error.message || error.name : 'Unknown error'}`);
        };

        clearReq.onsuccess = () => {
          this._getStoreCounts(tx, [this._pastTableName, this._futureTableName])
            .then(({ past }) => {
              resolve({
                record: undefined,
                pastCount: past,
                futureCount: 0,
              });
            })
            .catch((error) => reject(`Count error: ${error}`));
        };
      };

      tx.onerror = (e) => {
        const error = (e?.target as IDBRequest)?.error;
        reject(`Transaction error: ${error ? error.message || error.name : 'Unknown error'}`);
      };
    });
  }

  clear(): Promise<{
    record: undefined;
    pastCount: number;
    futureCount: number;
  }> {
    return new Promise((resolve, reject) => {
      if (!this._db) {
        return reject(`No database error`);
      }
      const tx = this._db.transaction([this._pastTableName, this._futureTableName], 'readwrite');
      const pastStore = tx.objectStore(this._pastTableName);
      const futureStore = tx.objectStore(this._futureTableName);

      const clearPastReq = pastStore.clear();

      clearPastReq.onerror = (e) => {
        const error = (e?.target as IDBRequest)?.error;
        reject(`Clear past error: ${error ? error.message || error.name : 'Unknown error'}`);
      };

      clearPastReq.onsuccess = () => {
        const clearFutureReq = futureStore.clear();

        clearFutureReq.onerror = (e) => {
          const error = (e?.target as IDBRequest)?.error;
          reject(`Clear future error: ${error ? error.message || error.name : 'Unknown error'}`);
        };

        clearFutureReq.onsuccess = () => {
          this._cleared.next();
          resolve({
            record: undefined,
            pastCount: 0,
            futureCount: 0,
          });
        };
      };

      tx.onerror = (e) => {
        const error = (e?.target as IDBRequest)?.error;
        reject(`Transaction error: ${error ? error.message || error.name : 'Unknown error'}`);
      };
    });
  }

  private _getStoreCounts(tx: IDBTransaction, storeNames: string[]): Promise<Record<string, number>> {
    const countPromises = storeNames.map((storeName) => {
      return new Promise<number>((resolve, reject) => {
        const countReq = tx.objectStore(storeName).count();
        countReq.onsuccess = () => resolve(countReq.result);
        countReq.onerror = (e) => reject(`Count error (${storeName}): ${(e?.target as IDBRequest)?.error}`);
      });
    });

    return Promise.all(countPromises).then((counts) => {
      return storeNames.reduce(
        (acc, store, i) => {
          acc[store] = counts[i];
          return acc;
        },
        {} as Record<string, number>
      );
    });
  }
}
